import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import frFR from 'antd/locale/fr_FR';
import enUS from 'antd/locale/en_US';
import fr from './locales/fr';
import en from './locales/en';

const LOCALE_KEY = 'app-locale';
const SUPPORTED = ['fr', 'en'];
const DEFAULT_LOCALE = 'fr';

// Catalogue actif + fallback FR
const CATALOGS = { fr, en };
export const ANTD_LOCALES = { fr: frFR, en: enUS };

const LocaleCtx = createContext({
    locale: DEFAULT_LOCALE,
    setLocale: () => {},
    t: (key) => key,
});

export const useLocale = () => useContext(LocaleCtx);

// Hook utilitaire : t(key, params?) — récupère la traduction et interpole {param}.
// Fallback en cas de clé manquante : essaye fr.js puis renvoie la clé brute.
export const useT = () => {
    const { t } = useContext(LocaleCtx);
    return t;
};

const interpolate = (str, params) => {
    if (!params || !str) return str;
    return str.replace(/\{(\w+)\}/g, (_, k) => (params[k] != null ? String(params[k]) : `{${k}}`));
};

const readStoredLocale = () => {
    try {
        const v = localStorage.getItem(LOCALE_KEY);
        return SUPPORTED.includes(v) ? v : DEFAULT_LOCALE;
    } catch { return DEFAULT_LOCALE; }
};

export const LocaleProvider = ({ children }) => {
    const [locale, setLocaleState] = useState(readStoredLocale);

    useEffect(() => {
        try { localStorage.setItem(LOCALE_KEY, locale); } catch { /* */ }
        if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('lang', locale);
        }
    }, [locale]);

    const setLocale = useCallback((next) => {
        if (SUPPORTED.includes(next)) setLocaleState(next);
    }, []);

    // t(key, params?) avec fallback FR si la clé manque dans la locale active
    const t = useCallback((key, params) => {
        if (!key) return '';
        const cat = CATALOGS[locale] || CATALOGS[DEFAULT_LOCALE];
        const value = cat[key] || CATALOGS[DEFAULT_LOCALE][key] || key;
        return interpolate(value, params);
    }, [locale]);

    const value = useMemo(() => ({ locale, setLocale, t, antdLocale: ANTD_LOCALES[locale] || frFR }), [locale, setLocale, t]);

    return (
        <LocaleCtx.Provider value={value}>
            {children}
        </LocaleCtx.Provider>
    );
};
